import { Box, Button, Checkbox, Group, Pagination, Space, Table, Text } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { useParams } from 'react-router-dom';

import CustomSearch from '../../../../../../ui/search/customSearch';
import { useGetAiAgentModelList } from '../../../../../../../shared/apis/ai-agent/model/reactQueries';
import useSearch from '../../../../../../../shared/hook/useSearch';

import type { IEquipmentModel, TAiAgentModelSearchType } from '../../../../../../../shared/apis/ai-agent/model/types';

interface IMappingTableProps {
  selectedModelIds: number[];
  onToggleModel: (model: IEquipmentModel) => void;
}

export default function MappingTable(props: IMappingTableProps) {
  const { selectedModelIds, onToggleModel } = props;
  const { companyId } = useParams();
  const { inputRef, searchType, setSearchType, searchWord, setSearchWord, page, setPage, handleSearch } =
    useSearch('assignedName');
  const size = 10;
  const {
    data: models,
    isLoading: isTableLoading,
    isError: isTableError,
  } = useGetAiAgentModelList(
    {
      companyId: Number(companyId),
      searchType: searchType as TAiAgentModelSearchType,
      keyword: searchWord || undefined,
      page,
      size,
      useYn: 'Y',
    },
    { suspense: false },
  );
  const modelList = models?.result || [];
  const totalPage = Math.ceil(Number(models?.count || 0) / size);
  const isAllChecked = modelList.length > 0 && modelList.every((model) => selectedModelIds.includes(model.modelId));

  const handleToggleAllModels = () => {
    const modelsToToggle = isAllChecked ? modelList : modelList.filter((model) => !selectedModelIds.includes(model.modelId));

    modelsToToggle.forEach(onToggleModel);
  };

  const resetSearchInput = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleResetSearchFilters = () => {
    resetSearchInput();
    setSearchType('assignedName');
    setSearchWord('');
    setPage(1);
  };

  return (
    <Box
      mt="md"
      py="md"
      style={(theme) => ({
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: theme.radius.sm,
      })}
    >
      <Group px="md" gap="xs" align="center">
        <CustomSearch
          resetSearchInput={resetSearchInput}
          resetSearch={handleResetSearchFilters}
          searchType={searchType}
          setSearchType={setSearchType}
          inputRef={inputRef}
          handleSearch={handleSearch}
          searchOptions={[
            {
              value: 'assignedName',
              label: '모델명',
            },
            {
              value: 'productGroup',
              label: '제품군명',
            },
          ]}
          placeholder="검색어를 입력하세요."
          showResetButton={false}
        />
        <Button
          variant="transparent"
          size="compact-xs"
          leftSection={<IconRefresh size={16} />}
          onClick={handleResetSearchFilters}
        >
          초기화
        </Button>
      </Group>
      <Space h="md" />
      <Table.ScrollContainer minWidth={300}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Checkbox checked={isAllChecked} onChange={handleToggleAllModels} />
              </Table.Th>
              <Table.Th>번호</Table.Th>
              <Table.Th>모델명 (모델코드)</Table.Th>
              <Table.Th>제품군명</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {!isTableLoading &&
              !isTableError &&
              modelList.map((item, index) => (
                <Table.Tr key={item.modelId}>
                  <Table.Td>
                    <Checkbox checked={selectedModelIds.includes(item.modelId)} onChange={() => onToggleModel(item)} />
                  </Table.Td>
                  <Table.Td>{item.modelId}</Table.Td>
                  <Table.Td ta="left" maw={200}>
                    <Text size="sm" truncate>
                      {item.assignedName} ({item.model})
                    </Text>
                  </Table.Td>
                  <Table.Td ta="left" maw={200}>
                    <Text size="sm" truncate>
                      {item.productGroup || '-'}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            {(isTableLoading || isTableError || !modelList.length) && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text ta="center" c={isTableError ? 'red' : 'dimmed'} py="md" size="sm">
                    {isTableLoading
                      ? '불러오는 중입니다.'
                      : isTableError
                        ? '기계 모델을 불러오지 못했습니다'
                        : '검색 결과가 없습니다.'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Group justify="center" mt="xs">
        <Pagination total={totalPage} value={page} onChange={setPage} />
      </Group>
    </Box>
  );
}
